import { memo, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { DataGridPremium } from "@mui/x-data-grid-premium";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";

import { useOrganizationStore } from "stores/OrganizationStore";
import { useUserStore } from "stores/UserStore";

import { GenericObject, isPermissionLevel } from "utility/misc";

import { LoginMethod, OrganizationMembersColumns } from "./data";
import OrganizationMembersDeleteConfirmationModal from "./DeleteConfirmationModal";

// -----------------------------------------------------------------------------

const GreedySpace: RegExp = /\s+/g;
const SearchableKeys: string[] = ["email", "permission_level", "full_name"];

// -----------------------------------------------------------------------------

const OrganizationMembersList = memo(
  function OrganizationMembersList(): JSX.Element {
    const [userStore] = useUserStore();
    const [organizationStore, organizationActions] = useOrganizationStore();

    //
    const [memberToDelete, setMemberToDelete] = useState(null as any);
    const [filterText, setFilterText] = useState("");
    const [openEdit, setOpenEdit] = useState(false);
    const [editMemberUuid, setEditMemberUuid] = useState("");
    const [editMemberName, setEditMemberName] = useState("");
    const [editMemberRole, setEditMemberRole] = useState("");
    const [editMemberLogin, setEditMemberLogin] = useState("");
    const [, setEditLoading] = useState(false);

    //
    const deleteMember = (index: number) => {
      setMemberToDelete(null);
      if (index < 1) {
        return;
      }

      toast.dismiss();
      organizationActions.deleteMember(memberToDelete, () => {});
    };

    //
    useEffect(() => {
      if (organizationStore?.memberDeleted?.success === null) {
        return;
      }

      organizationStore?.memberDeleted?.success
        ? toast.success(
            `Deleted ${
              organizationStore?.memberDeleted?.member?.full_name ?? ""
            }`,
            {
              position: "bottom-center",
            }
          )
        : toast.error(
            `Failed to delete ${
              organizationStore?.memberDeleted?.member?.full_name ?? ""
            }`,
            {
              position: "bottom-center",
            }
          );
      organizationActions.resetMemberDeleted();
    }, [organizationStore.memberDeleted, organizationActions]);

    useEffect(() => {
      if (
        !organizationStore.membersLoadAttempted &&
        !organizationStore.membersLoading
      ) {
        organizationActions.loadMembers();
      }
    }, [organizationStore, organizationActions]);

    //
    const treatedFilterText = filterText.trim();
    const filterTerms = treatedFilterText
      ? treatedFilterText.split(GreedySpace)
      : [];
    const filteredMembers =
      filterTerms.length > 0
        ? (organizationStore.members ?? []).filter((member: GenericObject) =>
            SearchableKeys.some((key: string) =>
              filterTerms.some(
                (term: string) => member[key as any]?.includes(term) === true
              )
            )
          )
        : organizationStore.members ?? [];
    const members: any[] = filteredMembers.map((member: any) => {
      return {
        ...member,
        actions: (
          <>
            <IconButton onClick={() => openEditDialog(member)}>
              <EditIcon style={{ cursor: "pointer" }} />
            </IconButton>

            {(isPermissionLevel(userStore, "Admin") ||
              isPermissionLevel(userStore, "SuperAdmin")) &&
            member.email !== userStore.user.email ? (
              <IconButton onClick={() => setMemberToDelete(member)}>
                <DeleteOutlineIcon />
              </IconButton>
            ) : null}
          </>
        ),
      };
    });

    const getLoginMethodOptions = () => {
      if (
        Object.keys(organizationStore.organization.workos_conn_id_dict ?? {})
          .length === 0
      ) {
        return (
          <MenuItem value={editMemberLogin} key={editMemberLogin}>
            {LoginMethod[editMemberLogin]}
          </MenuItem>
        );
      }

      const options: any = [];
      Object.keys(organizationStore.organization.workos_conn_id_dict ?? {}).map(
        (method) => {
          return options.push(
            <MenuItem value={method} key={method}>
              {LoginMethod[method]}
            </MenuItem>
          );
        }
      );
      return options;
    };

    const openEditDialog = (member: any) => {
      setOpenEdit(true);
      setEditMemberUuid(member.uuid);
      setEditMemberName(member.full_name);
      setEditMemberRole(member.permission_level);
      setEditMemberLogin(member.login_method);
    };

    const resetEdit = () => {
      setEditLoading(false);
      setEditMemberUuid("");
      setEditMemberName("");
      setEditMemberRole("");
      setEditMemberLogin("");
      setOpenEdit(false);
    };

    const closeEditDialog = () => {
      resetEdit();
    };

    const saveEdit = () => {
      setEditLoading(true);
      organizationActions.editMember(
        editMemberUuid,
        {
          permission_level: editMemberRole,
          login_method: editMemberLogin,
        },
        onEditSuccess,
        onEditError
      );
      closeEditDialog();
    };

    const onEditSuccess = () => {
      resetEdit();
      organizationActions.loadMembers();
      toast.success(`Successfully updated the member`, {
        position: "bottom-center",
      });
    };

    const onEditError = () => {
      resetEdit();
      toast.success(`Something went wrong updating the member`, {
        position: "bottom-center",
      });
    };

    //
    return (
      <>
        <Paper
          elevation={1}
          sx={{
            p: 0,
            width: "100%",
            height: "100%",
            minHeight: 512,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {memberToDelete ? (
            <OrganizationMembersDeleteConfirmationModal
              name={memberToDelete.full_name}
              onClose={deleteMember}
            />
          ) : null}
          <Box
            mx={{ display: "flex", justifyContent: "flex-end", margin: "8px" }}
          >
            <TextField
              variant="outlined"
              size="small"
              placeholder="Filter users"
              InputProps={{
                endAdornment: filterText ? (
                  <InputAdornment position="end">
                    <CancelIcon
                      onClick={() => setFilterText("")}
                      style={{ cursor: "pointer" }}
                    />
                  </InputAdornment>
                ) : null,
              }}
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
            />
          </Box>
          <DataGridPremium
            getRowId={(row) => row.uuid}
            rows={members}
            rowCount={members.length}
            columns={OrganizationMembersColumns}
            density="compact"
            page={0}
            pageSize={20}
            rowsPerPageOptions={[20]}
            checkboxSelection={false}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            sx={MemberTableSX}
            paginationMode="server"
            sortingMode="server"
            loading={organizationStore.membersLoading}
          />
        </Paper>

        <Dialog open={openEdit} onClose={closeEditDialog}>
          <DialogTitle>Edit Member</DialogTitle>

          <DialogContent>
            <Stack spacing={2}>
              <DialogContentText>
                Edit {editMemberName}&apos;s permission level and login method.
              </DialogContentText>

              <FormControl fullWidth>
                <InputLabel id="select-role-label">Role</InputLabel>
                <Select
                  labelId="select-role-label"
                  id="select-role"
                  value={editMemberRole}
                  label="Role"
                  onChange={(event) => {
                    setEditMemberRole(event.target.value);
                  }}
                >
                  <MenuItem value={"SuperAdmin"}>Super Admin</MenuItem>
                  <MenuItem value={"Admin"}>Admin</MenuItem>
                  <MenuItem value={"Auditor"}>Auditor</MenuItem>
                  <MenuItem value={"UnprivilegedUser"}>Member</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="select-login-label">Login Method</InputLabel>
                <Select
                  labelId="select-login-label"
                  id="select-login"
                  value={editMemberLogin}
                  label="Login Method"
                  onChange={(event) => {
                    setEditMemberLogin(event.target.value);
                  }}
                >
                  {getLoginMethodOptions()}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeEditDialog}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default OrganizationMembersList;

const MemberTableSX = {
  border: 0,

  "& .MuiDataGrid-cell": {
    wordBreak: "break-all",

    "&:focus": {
      outline: "none",
    },
  },

  "& .MuiDataGrid-columnHeader": {
    "&:focus": {
      outline: "none",
    },
  },

  "& .MuiDataGrid-columnHeaderTitleContainer": {
    padding: 0,
  },

  "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
    display: "none",
  },
};
